<template>
  <div class="information-section">
    <!-- PARAFFIN BLOCK -->
    <div v-if="portion.portionType.potOriginalName === 'entity.portiontype.paraffinblock'">
      <b-row class="d-flex align-items-center mb-2 mt-1 px-2">
        <b-col>
          <span class="ap-title ap-title__main">{{ $tc('globalLocator.sections.portionData') }}</span>
        </b-col>
      </b-row>

      <b-row v-if="portion.depleted">
        <b-col>
          <div class="block-depleted-card d-flex align-items-center py-3 m-2">
            <h3 class="heading-3">
              <i
                class="icon-block-slash mx-2"
                aria-hidden="true"
              />{{ $tc('grid.detail.depletedBlock') }}
            </h3>
          </div>
        </b-col>
      </b-row>

      <b-form-row class="px-2 pt-2">
        <b-col lg="3">
          <div class="info-card">
            <b-form-row class="h-100">
              <b-col class="flex-row-center">
                <section-info-col-type
                  :label="$tc('globalLocator.labels.laboratory')"
                  :data="portion.currentLaboratory"
                  col-class="flex-column-center"
                >
                  <template #default="{ data }">
                    {{ data.labName }}
                  </template>
                </section-info-col-type>
              </b-col>
            </b-form-row>
          </div>
        </b-col>
        <b-col>
          <div class="info-card">
            <b-form-row class="h-100">
              <b-col class="flex-row-center">
                <section-info-col-type-date
                  :label="$tc('globalLocator.labels.creationDate')"
                  :date="portion.requestDate"
                  col-class="flex-column-center"
                />
              </b-col>
              <b-col class="flex-row-center">
                <section-info-col-type-date
                  :label="$tc('globalLocator.labels.processorEntryDate')"
                  :date="portion.processingStartDate"
                  col-class="flex-column-center"
                />
              </b-col>
              <b-col class="flex-row-center">
                <section-info-col-type-date
                  :label="$tc('globalLocator.labels.processorDepartureDate')"
                  :date="portion.processingEndDate"
                  col-class="flex-column-center"
                />
              </b-col>
              <b-col class="flex-row-center">
                <section-info-col-type-date
                  :label="$tc('globalLocator.labels.confectionDate')"
                  :date="portion.generationDate"
                  col-class="flex-column-center"
                />
              </b-col>
            </b-form-row>
          </div>
        </b-col>
      </b-form-row>

      <b-row class="mt-4 pt-1 px-2">
        <b-col>
          <section-info-col-type-personnel
            :label="$tc('globalLocator.labels.processorEntryTechnician')"
            :personnel="portion.processingStartPersonnel"
          />
        </b-col>
        <b-col>
          <section-info-col-type-personnel
            :label="$tc('globalLocator.labels.processorDepartureTechnician')"
            :personnel="portion.processingEndPersonnel"
          />
        </b-col>
        <b-col>
          <section-info-col-type-personnel
            :label="$tc('globalLocator.labels.confectionTechnician')"
            :personnel="portion.generationResponsiblePersonnel"
          />
        </b-col>
      </b-row>

      <b-row class="px-2 pt-4">
        <b-col>
          <hr>
        </b-col>
      </b-row>

      <b-row class="px-2 mt-1">
        <b-col cols="3">
          <section-info-col-type
            :label="$tc('globalLocator.labels.portionType')"
            :data="portion.portionType"
          >
            <template #default="{ data }">
              {{ data.potName }}
            </template>
          </section-info-col-type>
        </b-col>
        <b-col cols="2">
          <section-info-col-type
            :label="$tc('globalLocator.labels.fragments')"
            :data="portion.fragments"
          />
        </b-col>
        <b-col cols="2">
          <section-info-col-type
            :label="$tc('globalLocator.labels.orientation')"
            :data="portion.fragmentOrientation"
          >
            <template #default="{ data }">
              <i
                :class="`${data.forIconName} main`"
                aria-hidden="true"
              />
              {{ data.forDescription }}
            </template>
          </section-info-col-type>
        </b-col>
        <b-col cols="5">
          <section-info-col-type
            :label="$tc('globalLocator.labels.assignedProcessor')"
            :data="portion.tissueProcessor"
          />
        </b-col>
      </b-row>

      <b-row class="px-2 mt-4">
        <b-col cols="3">
          <section-info-col-type-date
            :label="$tc('globalLocator.labels.cancellationDate')"
            :date="portion.cancellationDate"
          />
        </b-col>
        <b-col cols="4">
          <section-info-col-type-personnel
            :label="$tc('globalLocator.labels.cancellationTechnician')"
            :personnel="portion.cancellationTechnicianPersonnel"
          />
        </b-col>
      </b-row>

      <b-row class="px-2 mt-4">
        <b-col cols="12">
          <section-info-col-type
            :label="$tc('globalLocator.labels.observations')"
            :data="portion.notes"
          />
        </b-col>
      </b-row>
    </div>

    <!-- NON-PARAFFIN (FROZEN) BLOCK -->
    <div v-else>
      <b-row class="d-flex align-items-center mb-2 mt-1 px-2">
        <b-col>
          <span class="title">{{ $tc('globalLocator.sections.portionData') }}</span>
        </b-col>
      </b-row>

      <b-row v-if="portion.depleted">
        <b-col>
          <div class="block-depleted-card d-flex align-items-center py-3 m-2">
            <h3 class="heading-3">
              <i
                class="icon-block-slash mx-2"
                aria-hidden="true"
              />{{ $tc('grid.detail.depletedBlock') }}
            </h3>
          </div>
        </b-col>
      </b-row>

      <b-row class="px-2 pt-2">
        <b-col>
          <section-info-col-type-date
            :label="$tc('globalLocator.labels.creationDate')"
            :date="portion.requestDate"
          />
        </b-col>
        <b-col>
          <section-info-col-type
            :label="$tc('globalLocator.labels.portionType')"
            :data="portion.portionType"
          >
            <template #default="{ data }">
              {{ data.potName }}
            </template>
          </section-info-col-type>
        </b-col>
        <b-col>
          <section-info-col-type-personnel
            :label="$tc('globalLocator.labels.confectionTechnician')"
            :personnel="portion.generationResponsiblePersonnel"
          />
        </b-col>
      </b-row>

      <b-row class="px-2 pt-4">
        <b-col>
          <hr>
        </b-col>
      </b-row>

      <b-row class="px-2 mt-1">
        <b-col cols="2">
          <section-info-col-type
            :label="$tc('globalLocator.labels.fragments')"
            :data="portion.fragments"
          />
        </b-col>
        <b-col cols="3">
          <section-info-col-type-date
            :label="$tc('globalLocator.labels.cancellationDate')"
            :date="portion.cancellationDate"
          />
        </b-col>
        <b-col cols="4">
          <section-info-col-type-personnel
            :label="$tc('globalLocator.labels.cancellationTechnician')"
            :personnel="portion.cancellationTechnicianPersonnel"
          />
        </b-col>
      </b-row>

      <b-row class="px-2 mt-4">
        <b-col cols="12">
          <section-info-col-type
            :label="$tc('globalLocator.labels.observations')"
            :data="portion.notes"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import SectionInfoColType from '@/modules/globalLocatorModule/sections/info/col-types/section-info-col-type.vue'
import SectionInfoColTypeDate from '@/modules/globalLocatorModule/sections/info/col-types/date/section-info-col-type-date.vue'
import SectionInfoColTypePersonnel from '@/modules/globalLocatorModule/sections/info/col-types/personnel/section-info-col-type-personnel.vue'

export default {
    name: 'PortionInfoSection',
    components: {
        SectionInfoColType,
        SectionInfoColTypeDate,
        SectionInfoColTypePersonnel
    },
    props: {
        portion: { type: Object, required: true }
    }
}
</script>

<style lang="scss">
    .information-section {
        .block-depleted-card {
            border: 1px solid vt-color(grid-general-border);
            border-radius: 3px;
            border-left: 3px solid vt-color(primary);
            background: vt-color(primary, .95);

            h3 {
                margin-bottom: 0;
                font-size: font-size(normal);
                font-style: italic;
            }

            span {
                font-size: font-size(small);
            }
        }
    }
</style>
